/**
 * Registering right where a guest is (D41): the post-fight popup offers the email field and Google
 * and Facebook straight away. The email's code goes out as the sign-in sheet opens at its code
 * step; Google and Facebook leave for the provider's page, as from the sheet.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { AuthProblem } from '../../net/account';
import { socialProviders, startSocial, type SocialProvider } from '../../net/social';
import { diag } from '../../platform/diag';
import { useApp } from '../../state/store';
import { EMAIL, GoogleMark, PROVIDER_NAMES } from './AuthSheet';
import s from './AuthSheet.module.css';
import q from './QuickRegister.module.css';
import ui from './ui.module.css';

export function QuickRegister({ onLeave }: { onLeave: () => void }) {
  const { t } = useTranslation();
  const openAuth = useApp((st) => st.openAuth);
  const [email, setEmail] = useState('');
  const [problem, setProblem] = useState<AuthProblem | null>(null);
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState<Record<SocialProvider, boolean>>({ google: false, facebook: false });

  useEffect(() => {
    void socialProviders().then(setProviders);
  }, []);

  const send = (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!EMAIL.test(address)) return setProblem('badEmail');
    onLeave();
    openAuth('register', address);
  };

  const social = async (provider: SocialProvider) => {
    setBusy(true);
    setProblem(null);
    try {
      await startSocial(provider, 'register');
    } catch (err) {
      diag.error(`auth: ${String(err)}`);
      setProblem('socialFailed');
      setBusy(false);
    }
  };

  return (
    <div className={q.root}>
      <form className={q.emailRow} onSubmit={send} noValidate>
        <input
          className={[s.input, q.input].join(' ')}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={t('screens.auth.email')}
          aria-label={t('screens.auth.email')}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setProblem(null);
          }}
        />
        <button type="submit" className={[ui.primary, q.send].join(' ')} disabled={busy}>
          {t('screens.auth.sendCode')}
        </button>
      </form>
      {problem && (
        <p className={s.error} role="alert">
          {t(`screens.auth.errors.${problem}`)}
        </p>
      )}
      {(providers.google || providers.facebook) && (
        <>
          <div className={s.or}>{t('screens.auth.or')}</div>
          <div className={s.social}>
            {(['google', 'facebook'] as const)
              .filter((p) => providers[p])
              .map((p) => (
                <button
                  key={p}
                  type="button"
                  className={[s.provider, q.provider, p === 'google' ? s.google : s.facebook].join(' ')}
                  aria-label={t('screens.auth.withProvider', { provider: PROVIDER_NAMES[p] })}
                  disabled={busy}
                  onClick={() => void social(p)}
                >
                  {p === 'google' ? <GoogleMark /> : <span className={s.fbMark}>f</span>}
                  {PROVIDER_NAMES[p]}
                </button>
              ))}
          </div>
        </>
      )}
      <p className={[s.agree, q.agree].join(' ')}>
        <Trans
          i18nKey="screens.auth.agree"
          components={{
            terms: <a href="/terms" target="_blank" rel="noopener" />,
            privacy: <a href="/privacy" target="_blank" rel="noopener" />,
          }}
        />
      </p>
    </div>
  );
}
